import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import type { DocumentType, DocumentTypeSection } from '../../../core/types/data-models';
import { AssistantDocumentCard, type AssistantDocStats } from './AssistantDocumentCard';
import { AssistantHeaderBlock } from './AssistantHeaderBlock';
import { RecommendedDocumentCard } from './RecommendedDocumentCard';
import { BlockedCategoryNotice, RemainingDocTypesCard } from './RemainingDocTypes';
import type { AssistantRoleData } from './assistantRoles';

interface AssistantRoleRowProps extends AssistantRoleData {
  statsFor: (docId: number) => AssistantDocStats;
  /** Each doc type's top-level sections, keyed by DocumentType.type — previewed on recommended cards. */
  rootSectionsByDocType: Map<string, DocumentTypeSection[]>;
  canGenerate: boolean;
  onOpenDocument: (id: number) => void;
  onGenerateRecommended: (docType: DocumentType) => void;
}

/** Row layout: one row per role — the header block beside its documents laid out left-to-right as cards. */
export function AssistantRoleRow({
  role,
  relatedDocs,
  recommended,
  blocked,
  remaining,
  statsFor,
  rootSectionsByDocType,
  canGenerate,
  onOpenDocument,
  onGenerateRecommended,
}: AssistantRoleRowProps) {
  const hasCards = relatedDocs.length > 0 || recommended.length > 0 || remaining > 0;

  return (
    <GlassPanel
      hover
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: { xs: 'column', xl: 'row' },
        gap: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: { xs: '100%', xl: 288 },
          flexShrink: 0,
          display: 'flex',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: alpha(role.color ?? '#6366f1', 0.4),
        }}
      >
        <AssistantHeaderBlock
          role={role}
          hasDocuments={relatedDocs.length > 0}
          docCount={relatedDocs.length}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          justifyContent: hasCards ? 'flex-start' : 'center',
        }}
      >
        {relatedDocs.length === 0 && recommended.length === 0 && (
          blocked ? (
            <BlockedCategoryNotice blocked={blocked} />
          ) : remaining === 0 ? (
            <Typography variant="caption" color="text.disabled">
              No documents yet
            </Typography>
          ) : null
        )}
        {hasCards && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: 1.5,
            }}
          >
            {relatedDocs.map((doc) => (
              <AssistantDocumentCard
                key={`doc-${doc.id}`}
                doc={doc}
                stats={statsFor(doc.id)}
                onOpen={() => onOpenDocument(doc.id)}
              />
            ))}
            {recommended.map(({ docType: dt }) => (
              <RecommendedDocumentCard
                key={`rec-${dt.id}`}
                docType={dt}
                sections={rootSectionsByDocType.get(dt.type) ?? []}
                canGenerate={canGenerate}
                onGenerate={() => onGenerateRecommended(dt)}
                roleColor={role.color ?? '#6366f1'}
              />
            ))}
            {remaining > 0 && (
              <RemainingDocTypesCard count={remaining} roleColor={role.color ?? '#6366f1'} />
            )}
          </Box>
        )}
      </Box>
    </GlassPanel>
  );
}
